// Mock database service for development
import API_BASE_URL, { getApiServerHint } from "../config/api";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

class DatabaseService {
  async request(endpoint, options = {}, retryCount = 0) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add timeout - increased for large image uploads
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second timeout for large uploads

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const body = await response.text();
        if (
          response.status === 500 &&
          (body.includes('Proxy error') || body.includes('ECONNREFUSED'))
        ) {
          throw new Error(
            'Backend not running. Start it first: cd backend && python app.py (port 5001)'
          );
        }
        if (contentType.includes('application/json') && body) {
          try {
            const json = JSON.parse(body);
            throw new Error(json.error || json.detail || `HTTP error! status: ${response.status}`);
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.startsWith('HTTP error')) {
              throw parseErr;
            }
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      const isNetworkError = !error.response && (error.message === 'Failed to fetch' || error.name === 'TypeError');
      const canRetry = isNetworkError && retryCount < MAX_RETRIES;

      if (canRetry) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        return this.request(endpoint, options, retryCount + 1);
      }

      console.error('Database request failed:', error);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - server is taking too long to respond');
      }
      if (isNetworkError) {
        console.error(`Cannot reach backend. ${getApiServerHint()}`);
      }
      throw error;
    }
  }

  // Test Requests
  async getTestRequests() {
    try {
      const response = await this.request(`/test-requests`);
      console.log('🔍 Database Service - Raw response:', response);
      console.log('🔍 Database Service - response.test_requests:', response.test_requests?.length || 'undefined');
      const result = response.test_requests || response;
      console.log('🔍 Database Service - Final result:', result?.length || 'undefined');
      return result; // Handle both old and new format
    } catch (error) {
      console.error('Failed to fetch test requests:', error);
      return [];
    }
  }

  async getTestRequestById(id) {
    try {
      return await this.request(`/test-requests/${id}`);
    } catch (error) {
      console.error('Failed to fetch test request:', error);
      return null;
    }
  }

  // Customers
  async getCustomers() {
    try {
      return await this.request('/customers');
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      return [];
    }
  }

  async getCustomerById(id) {
    try {
      return await this.request(`/customers/${id}`);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      return null;
    }
  }

  // Samples/Test Results
  async getSamples() {
    try {
      return await this.request('/samples');
    } catch (error) {
      console.error('Failed to fetch samples:', error);
      return [];
    }
  }

  async getTestResults(testRequestId) {
    try {
      return await this.request(`/test-results/${testRequestId}`);
    } catch (error) {
      console.error('Failed to fetch test results:', error);
      return null;
    }
  }

  // Test Observations
  async saveTestObservations(testRequestId, observationsData) {
    try {
      return await this.request(`/test-observations/${testRequestId}`, {
        method: 'POST',
        body: JSON.stringify(observationsData),
      });
    } catch (error) {
      console.error('Failed to save test observations:', error);
      throw error;
    }
  }

  async getTestObservations(testRequestId) {
    try {
      return await this.request(`/test-observations/${testRequestId}`);
    } catch (error) {
      console.error('Failed to fetch test observations:', error);
      return null;
    }
  }

  // PDF Data - Fetch complete data for PDF generation
  async getTestRequestPDFData(testRequestId) {
    try {
      console.log(`📄 Fetching PDF data for test request: ${testRequestId}`);
      const response = await this.request(`/test-requests/${testRequestId}/pdf-data`);
      console.log('✅ PDF data fetched successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to get PDF data:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        testRequestId: testRequestId
      });
      
      // Provide more specific error messages
      if (error.message.includes('404')) {
        throw new Error(`Test request with ID ${testRequestId} not found in database`);
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Cannot connect to backend server. Make sure Flask is running on port 5000');
      } else {
        throw error;
      }
    }
  }

  // Strength Graph Data - Save strength graph data and observations
  async saveStrengthGraphData(testRequestId, strengthData) {
    try {
      console.log(`📊 Saving strength graph data for test request: ${testRequestId}`);
      const response = await this.request(`/strength-graph/${testRequestId}`, {
        method: 'POST',
        body: JSON.stringify(strengthData),
      });
      console.log('✅ Strength graph data saved successfully:', response);
      return response;
    } catch (error) {
      console.error('Failed to save strength graph data:', error);
      throw error;
    }
  }

  // PDF Generation
  async generatePDF(testRequestId) {
    try {
      const response = await this.request(`/generate-pdf/${testRequestId}`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      throw error;
    }
  }
}

export default new DatabaseService();
